import { PALETTE } from '../designPalette';
export const PLAN_CONFIG = {
    starter: { name: 'Starter', maxSeats: 1 },
    team: { name: 'Team', maxSeats: 5 },
    pro: { name: 'Pro', maxSeats: 20 },
};
/** Returns true when the user has an active paid plan (or active trial). */
export function hasActivePlan(plan) {
    if (!plan)
        return false;
    return plan in PLAN_CONFIG;
}
/**
 * Extra seats (Emily, 2026-10-07): Starter and Team keep their bundled seats
 * and can buy more at $12 per user. Mirrors `extraSeat` in
 * docs/pricing-reference.json; extraSeats.test.ts pins the two together.
 */
export const EXTRA_SEAT_PLAN_SLUGS = new Set(['starter', 'team']);
export const EXTRA_SEAT_MONTHLY_PRICE_USD = 12;
/** Stripe Checkout's default adjustable maximum; one ceiling everywhere a count is accepted. */
export const MAX_EXTRA_SEATS = 99;
/**
 * Per-plan caps the server enforces, in ONE table (Emily, 2026-10-22), so the
 * marketing comparison table and the help corpus can be checked against code:
 * `pnpm plan-limits:check`. -1 means no cap. `none` is a team with no plan
 * (trial over or subscription lapsed). A trial resolves to the Team plan for
 * review links and MCP scans (DEFAULT_NEW_USER_PLAN in billing.service.ts);
 * scheduled scans count a trial as its own tier.
 */
export const PLAN_LIMITS = {
    /** Active client review links per project (services/clientReview/reviews.ts). */
    reviewLinksPerProject: { starter: 10, team: 20, pro: -1, none: 3 },
    /** Scheduled scans per team (services/scheduledScanLimits.service.ts). */
    scheduledScans: { trial: 5, starter: 5, team: 10, pro: 25 },
    /** MCP scans per user per UTC day (services/mcpScanUsage.service.ts). */
    mcpScansPerDay: { starter: 10, team: 30, pro: -1, none: 3 },
};
export function planAllowsExtraSeats(plan) {
    return !!plan && EXTRA_SEAT_PLAN_SLUGS.has(plan) && hasActivePlan(plan);
}
/**
 * The seat cap: the plan's bundle, plus any seats bought on top of it. A
 * plan that cannot buy seats ignores the count, and a corrupt count can
 * neither shrink the bundle nor exceed MAX_EXTRA_SEATS.
 */
export function getSeatLimit(plan, extraSeats = 0) {
    if (!plan)
        return 0;
    const bundled = PLAN_CONFIG[plan]?.maxSeats ?? 0;
    if (!planAllowsExtraSeats(plan) || !Number.isFinite(extraSeats))
        return bundled;
    return bundled + Math.min(MAX_EXTRA_SEATS, Math.max(0, Math.trunc(extraSeats)));
}
export function getPlanName(plan) {
    if (!plan)
        return 'No plan';
    return PLAN_CONFIG[plan]?.name ?? plan;
}
/**
 * Canonical monthly list price in whole USD for each paid plan.
 *
 * Source of truth is docs/pricing-reference.json (the file `pnpm
 * billing:check-drift` compares against the live Clerk API). This map mirrors
 * its `monthlyFee` values so the server can attach a `$revenue` amount to the
 * `paid_checkout_completed` event without a Clerk round-trip on the webhook hot
 * path. paidCheckout.analytics.test.ts asserts the two never drift.
 */
export const PLAN_MONTHLY_PRICE_USD = {
    starter: 39,
    team: 79,
    pro: 249,
};
/**
 * Monthly list price in USD for a plan slug, or null for no plan / an
 * unrecognized slug. Used to populate `$revenue` on the revenue event; a null
 * return means "fire the event but omit the amount", never "drop the event".
 */
export function getPlanMonthlyPriceUsd(plan) {
    if (!plan)
        return null;
    return PLAN_MONTHLY_PRICE_USD[plan] ?? null;
}
/**
 * Plans sold by sales, with no self-serve checkout (Emily, 2026-09-14: Pro is
 * sales-assisted on the marketing site and in the app). ONE list, read by the
 * dashboard grid (which renders Contact sales) and by the server (which
 * refuses to open a checkout for them), so hiding the button can never be the
 * only thing standing between a crafted request and a Pro checkout.
 */
export const SALES_ASSISTED_PLAN_SLUGS = new Set(['pro']);
/**
 * Stripe subscription statuses that keep a team's access. `past_due` stays IN:
 * Stripe is still retrying the card, and its own guidance is to revoke on
 * `canceled` / `unpaid`, not before
 * (docs.stripe.com/billing/subscriptions/webhooks, read 2026-09-18).
 */
export const STRIPE_ACCESS_STATUSES = new Set(['active', 'trialing', 'past_due']);
export function stripeStatusGrantsAccess(status) {
    return !!status && STRIPE_ACCESS_STATUSES.has(status);
}
/**
 * Brand blue, for the surfaces that cannot read a CSS token: today the Stripe
 * hosted checkout page, which the server brands over HTTP.
 *
 * The canonical declaration lives in shared design-tokens/core.json. The
 * generated PALETTE also supplies the extension and Stripe branding.
 */
export const BRAND_BLUE_HEX = PALETTE.brand.primary;
/**
 * How Stripe paints the hosted checkout page (Emily, 2026-09-18: "make sure
 * stripe bg is our branded blue for checkout"). Sent per session as
 * `branding_settings`, not only configured in the Stripe Dashboard, so the
 * colour ships with the code and cannot be forgotten when the account is set up.
 *
 * Colours and a name ONLY. Stripe also takes `font_family` and `border_style`,
 * both enums whose permitted values are not in the docs this was written from,
 * and Stripe REFUSES an unknown or invalid parameter rather than ignoring it,
 * so a guessed enum would 400 every checkout. Our own font (Plus Jakarta Sans)
 * is not in Stripe's list either; a near-miss font reads worse than Stripe's
 * neutral default. Both stay unset until a real key can confirm them.
 *
 * White on this background measures 4.69:1 (WCAG AA for normal text), pinned
 * in the same test with the repo's own contrastRatio.
 */
export const STRIPE_CHECKOUT_BRANDING = {
    backgroundColor: BRAND_BLUE_HEX,
    buttonColor: BRAND_BLUE_HEX,
    /** The Stripe account's legal name may be a person's; the page must say OverlayQA. */
    displayName: 'OverlayQA',
};
//# sourceMappingURL=billing.js.map